import React, { useState } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TableCell, TableRow, FormLabel, Chip, TableBody, FormControl, Table, Grid } from '@material-ui/core';
import { RxOrder, RxOrderStatusEnum, Status } from 'openapi';
import StatusColumn from '../labels/StatusColumn';
import EditDeleteButtons from '../buttons/EditDeleteButtons';
import RxOrderContainer from '../../containers/RxOrder';
import CommonContainer from '../../containers/Common';
import UploadButton from '../buttons/UploadButton';
import MakerText from '../labels/MakerText';
import LensText from '../labels/LensText';
import ConfirmCancelButtons from '../buttons/ConfirmCancelButtons';
import ColorText from '../labels/ColorText';
import ColorTypeText from '../labels/ColorTypeText';
import DensityText from '../labels/DensityText';
import { Formik, Form, FormikHelpers } from 'formik';
import OptionText from '../labels/OptionText';
import KarteMemo from '../labels/KarteMemo';
import StatusPanel from '../panels/StatusPanel';
import dateFormat from 'dateformat';
import { FrameTypeEnum } from '../inputs/FrameTypeSelectFormik';
import ProcessingText from '../labels/ProcessingText';
import { ProcessingTypeEnum } from '../inputs/ProcessingTypeSelectFormik';
import { useTranslation } from 'react-i18next';

interface Props {
    rowData: RxOrder,
    onOrder: (rxOrder: RxOrder) => void,
    isLoading: boolean
}

interface FormValues {
    meganetLensRxOrderId: number,
    customerName: string,
    status: RxOrderStatusEnum,
    recordedDate: Date,
    leftMeganetLensMasterId: number,
    leftPd: number,
    leftSph: number,
    leftCyl: number,
    leftAx: number,
    leftAdd: number,
    leftOutDia: number,
    leftQuantity: number,
    rightMeganetLensMasterId: number,
    rightPd: number,
    rightSph: number,
    rightCyl: number,
    rightAx: number,
    rightAdd: number,
    rightOutDia: number,
    rightQuantity: number,
    meganetColorDensityMappingId: number,
    remarks: string,
    processingType: ProcessingTypeEnum,
    meganetFrameMasterId: number,
    degreeType: number,
}

const useStyles = makeStyles((theme: Theme) => createStyles({
    table: {
        minWidth: 650,
    },
    rowBlur: {
        opacity: 0.2,
        transition: 'all ease 300ms'
    },
    rowShow: {
        opacity: 1,
        transition: 'all ease 300ms'
    },
    formControl: {
        display: 'block'
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    tableSpacing: {
        marginRight: theme.spacing(5),
        marginLeft: theme.spacing(5),
    },
    tableCellSpacing: {
        padding: theme.spacing(1),
    },
}));

export default ((props) => {
    const {t} = useTranslation();

    const rxOrderContainer = RxOrderContainer.useContainer();
    const commonContainer = CommonContainer.useContainer();
    const [open, setOpen] = useState(false);
    const [statusList, setStatusList] = useState<Status[]>([]);
    const classes = useStyles({});

    const makerName = commonContainer.getMaker(props.rowData.leftMeganetLensMasterId)?.makerName;

    const handleEdit = (meganetLensRxOrderId: number) => {

        rxOrderContainer.setIsEditing(true);
        rxOrderContainer.setSelectedOrderId(meganetLensRxOrderId ? meganetLensRxOrderId : -1);
    };

    const handleDelete = (meganetLensRxOrderId: number) => {
        rxOrderContainer.setIsDeleting(true);
        rxOrderContainer.setSelectedOrderId(meganetLensRxOrderId ? meganetLensRxOrderId : -1);
    };

    const handleSumbit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
        (async () => {
            if (rxOrderContainer.isDeleting) {
                values.meganetLensRxOrderId = rxOrderContainer.selectedOrderId;
                // Delete row
                await rxOrderContainer.remove(values.meganetLensRxOrderId);
                rxOrderContainer.setIsDeleting(false);
            }
        })()

        formikHelpers.setSubmitting(false);
    }

    const handleCancel = (meganetLensRxOrderId: number | undefined) => {
        if (rxOrderContainer.isEditing) {
            rxOrderContainer.setIsEditing(false);
        }

        if (rxOrderContainer.isDeleting) {
            rxOrderContainer.setIsDeleting(false);
        }

        if (rxOrderContainer.isNewRow) {
            rxOrderContainer.setIsNewRow(false);
        }

        // Reset selectedOrderId
        rxOrderContainer.setSelectedOrderId(-1);
    }

    const handleUpload = async (selectedRxOrder: RxOrder) => {
        if (selectedRxOrder.processingType === ProcessingTypeEnum.Maker && !commonContainer.getFrameByFrameMasterId(selectedRxOrder.meganetFrameMasterId)) {
            alert('メーカー加工依頼する場合はフレームを選択してください。');
            return;
        }

        if (selectedRxOrder.processingType === ProcessingTypeEnum.Tanking && !(makerName === 'Seiko' || makerName === 'セイコー')) {
            alert('メーカー店舗トレースする場合は、セイコーのレンズのみできます。')
            return
        }

        props.onOrder(selectedRxOrder);
    }

    const handleStatus = async (orderId: number) => {
        // Show panel
        setOpen(true);

        // get status List
        var results = await rxOrderContainer.getStatusList(orderId);
        setStatusList(results ? results : []);
    }

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <StatusPanel handleClickOpen={() => handleStatus} handleClose={handleClose} open={open} statusList={statusList} />
            <TableRow className={rxOrderContainer.isEditing || rxOrderContainer.isNewRow || (rxOrderContainer.isDeleting && rxOrderContainer.selectedOrderId !== props.rowData.meganetLensRxOrderId) ? classes.rowBlur : classes.rowShow}>
                <TableCell>
                    <Formik
                        initialValues={props.rowData as FormValues}
                        onSubmit={handleSumbit}
                    >
                        {({ isSubmitting }) => (
                            <Form className={classes.form} noValidate>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell className={classes.tableCellSpacing} ><StatusColumn status={props.rowData.status} orderId={props.rowData.meganetLensRxOrderId} handleStatus={handleStatus} /></TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='karteNoLabel' style={{ fontSize: 13, }}>{props.rowData.karteNo ? t('karteNo') : ''}</FormLabel>
                                                    <br />
                                                    <FormLabel id='karteNo' style={{ color: 'black' }}>{props.rowData.karteNo !== undefined ? props.rowData.karteNo : ''}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='customerNameLabel' style={{ fontSize: 13, }}>{props.rowData.customerName ? t('customerName') : ''}</FormLabel>
                                                    <br />
                                                    <FormLabel id='customerName' style={{ color: 'black' }}>{props.rowData.customerName}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='shippingDateLabel' style={{ fontSize: 13, }}>{props.rowData.shippingDate ? t('shippingDate') : ''}</FormLabel>
                                                    <br />
                                                    <FormLabel id='shippingDate' style={{ color: 'black' }}>{props.rowData.shippingDate !== undefined ? dateFormat(props.rowData.shippingDate, 'yyyy/mm/dd') : ''}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='maker' style={{ fontSize: 13, }}>{t('maker')}</FormLabel>
                                                    <br />
                                                    <MakerText meganetLensMasterId={props.rowData.leftMeganetLensMasterId} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='color' style={{ fontSize: 13, }}>{t('color')}</FormLabel>
                                                    <br />
                                                    <ColorText meganetColorDensityMappingId={props.rowData.meganetColorDensityMappingId} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='color' style={{ fontSize: 13, }}>{t('colorType')}</FormLabel>
                                                    <br />
                                                    <ColorTypeText meganetColorDensityMappingId={props.rowData.meganetColorDensityMappingId} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='denTop' style={{ fontSize: 13, }}>{t('density')}</FormLabel>
                                                    <br />
                                                    <DensityText meganetColorDensityMappingId={props.rowData.meganetColorDensityMappingId} />
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <FormControl className={classes.formControl}>
                                                    <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label={t('right')} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl} style={{ maxWidth: 160 }}>
                                                    <FormLabel id='maker' style={{ fontSize: 13, }}>{t('lens')}</FormLabel>
                                                    <br />
                                                    <LensText meganetLensMasterId={props.rowData.rightMeganetLensMasterId} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightSph' style={{ fontSize: 13, }}>{`${t('right')} ${t('sph')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightSph' style={{ color: 'black' }}>{props.rowData.rightSph.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightCyl' style={{ fontSize: 13, }}>{`${t('right')} ${t('cyl')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightCyl' style={{ color: 'black' }}>{props.rowData.rightCyl.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightAx' style={{ fontSize: 13, }}>{`${t('right')} ${t('ax')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightAx' style={{ color: 'black' }}>{props.rowData.rightAx}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightAdd' style={{ fontSize: 13, }}>{`${t('right')} ${t('add')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightAdd' style={{ color: 'black' }}>{props.rowData.rightAdd.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightOutDia' style={{ fontSize: 13, }}>{`${t('right')} ${t('outDia')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightOutDia' style={{ color: 'black' }}>{props.rowData.rightOutDia}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='rightQuantity' style={{ fontSize: 13, }}>{`${t('right')} ${t('quantity')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='rightQuantity' style={{ color: 'black' }}>{props.rowData.rightQuantity}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                        {(props.rowData.rightMemo) ?
                                            <TableRow>
                                                <TableCell className={classes.tableCellSpacing}>
                                                    <FormControl className={classes.formControl}>
                                                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label={`${t('right')} ${t('memo')}`} />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} colSpan={7}>
                                                    <FormControl className={classes.formControl}>
                                                        <KarteMemo memo={props.rowData.rightMemo} />
                                                    </FormControl>
                                                </TableCell>
                                            </TableRow>
                                            :
                                            <></>
                                        }
                                        {makerName === 'HKO' ?
                                            <TableRow>
                                                <TableCell>
                                                    <FormControl className={classes.formControl}>
                                                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='HKO Right' />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='rightPd' style={{ fontSize: 13, }}>Right PD</FormLabel>
                                                        <br />
                                                        <FormLabel id='rightPd' style={{ color: 'black' }}>{props.rowData.rightPd}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='rightHeight' style={{ fontSize: 13, }}>Right Height</FormLabel>
                                                        <br />
                                                        <FormLabel id='rightHeight' style={{ color: 'black' }}>{props.rowData.rightHeight}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell colSpan={5} />
                                            </TableRow>
                                         : <></>
                                        }
                                        {(props.rowData.rightOptionItemIds && props.rowData.rightOptionItemIds.length > 0) && <OptionText isLoading={props.isLoading} optionItemIds={props.rowData.rightOptionItemIds} leftRight={0} pd={props.rowData.rightPd} label={`${t('right')} ${t('option')}`} />}
                                        <TableRow>
                                            <TableCell>
                                                <FormControl className={classes.formControl}>
                                                    <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label={t('left')} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='left'>
                                                <FormControl className={classes.formControl} style={{ maxWidth: 160 }}>
                                                    <FormLabel id='maker' style={{ fontSize: 13, }}>{t('lens')}</FormLabel>
                                                    <br />
                                                    <LensText meganetLensMasterId={props.rowData.leftMeganetLensMasterId} />
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftSph' style={{ fontSize: 13, }}>{`${t('left')} ${t('sph')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftSph' style={{ color: 'black' }}>{props.rowData.leftSph.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftCyl' style={{ fontSize: 13, }}>{`${t('left')} ${t('cyl')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftCyl' style={{ color: 'black' }}>{props.rowData.leftCyl.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftAx' style={{ fontSize: 13, }}>{`${t('left')} ${t('ax')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftAx' style={{ color: 'black' }}>{props.rowData.leftAx}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftAdd' style={{ fontSize: 13, }}>{`${t('left')} ${t('add')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftAdd' style={{ color: 'black' }}>{props.rowData.leftAdd.toFixed(2)}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftOutDia' style={{ fontSize: 13, }}>{`${t('left')} ${t('outDia')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftOutDia' style={{ color: 'black' }}>{props.rowData.leftOutDia}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='leftQuantity' style={{ fontSize: 13, }}>{`${t('left')} ${t('quantity')}`}</FormLabel>
                                                    <br />
                                                    <FormLabel id='leftQuantity' style={{ color: 'black' }}>{props.rowData.leftQuantity}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                        {(props.rowData.leftMemo) ?
                                            <TableRow>
                                                <TableCell className={classes.tableCellSpacing}>
                                                    <FormControl className={classes.formControl}>
                                                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label={`${t('left')} ${t('memo')}`} />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} colSpan={7}>
                                                    <FormControl className={classes.formControl}>
                                                        <KarteMemo memo={props.rowData.leftMemo} />
                                                    </FormControl>
                                                </TableCell>
                                            </TableRow>
                                            :
                                            <></>
                                        }
                                        {makerName === 'HKO' ?
                                            <TableRow>
                                                <TableCell>
                                                    <FormControl className={classes.formControl}>
                                                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='HKO Left' />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='leftPd' style={{ fontSize: 13, }}>Left PD</FormLabel>
                                                        <br />
                                                        <FormLabel id='leftPd' style={{ color: 'black' }}>{props.rowData.leftPd}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='leftHeight' style={{ fontSize: 13, }}>Left Height</FormLabel>
                                                        <br />
                                                        <FormLabel id='leftHeight' style={{ color: 'black' }}>{props.rowData.leftHeight}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell colSpan={5} />
                                            </TableRow> : <></>
                                        }
                                        {(props.rowData.leftOptionItemIds && props.rowData.leftOptionItemIds.length > 0) && <OptionText isLoading={props.isLoading} optionItemIds={props.rowData.leftOptionItemIds} leftRight={1} pd={props.rowData.leftPd} label={`${t('left')} ${t('option')}`} />}
                                        {makerName === 'HKO' ?
                                            <TableRow>
                                                <TableCell>
                                                    <FormControl className={classes.formControl}>
                                                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='HKO Frame' />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='frameType' style={{ fontSize: 13, }}>FrameType</FormLabel>
                                                        <br />
                                                        <FormLabel id='frameType' style={{ color: 'black' }}>{props.rowData.frameType === -1 ? '--' : Object.keys(FrameTypeEnum).find(key => FrameTypeEnum[key] === props.rowData.frameType) }</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='frameSizeA' style={{ fontSize: 13, }}>FrameSizeA</FormLabel>
                                                        <br />
                                                        <FormLabel id='frameSizeA' style={{ color: 'black' }}>{props.rowData.frameSizeA}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='frameSizeB' style={{ fontSize: 13, }}>FrameSizeB</FormLabel>
                                                        <br />
                                                        <FormLabel id='frameSizeB' style={{ color: 'black' }}>{props.rowData.frameSizeB}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell className={classes.tableCellSpacing} align='right'>
                                                    <FormControl className={classes.formControl}>
                                                        <FormLabel id='DBL' style={{ fontSize: 13, }}>DBL</FormLabel>
                                                        <br />
                                                        <FormLabel id='DBL' style={{ color: 'black' }}>{props.rowData.DBL}</FormLabel>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell colSpan={3} />
                                            </TableRow>
                                            : <></>
                                        }
                                        {(commonContainer.nationNo === '01' || commonContainer.nationNo === undefined) &&
                                            <ProcessingText rowData={props.rowData}/>
                                        }
                                        <TableRow>
                                            <TableCell className={classes.tableCellSpacing} align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                                            <TableCell className={classes.tableCellSpacing} align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                                            <TableCell className={classes.tableCellSpacing} colSpan={2} align='right'>
                                                <FormControl className={classes.formControl}>
                                                    <FormLabel id='remarks' style={{ fontSize: 13, }}>{props.rowData.remarks ? t('remarks') : ''}</FormLabel>
                                                    <br />
                                                    <FormLabel id='remarks' style={{ color: 'black' }}>{props.rowData.remarks}</FormLabel>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classes.tableCellSpacing} colSpan={2} align='left'>
                                                {(props.rowData.status === RxOrderStatusEnum.Waiting || props.rowData.status === RxOrderStatusEnum.Error) ?
                                                    (rxOrderContainer.isEditing || rxOrderContainer.isDeleting) && rxOrderContainer.selectedOrderId === props.rowData.meganetLensRxOrderId ?
                                                        <FormControl className={classes.formControl}>
                                                            <ConfirmCancelButtons meganetLensRxOrderId={props.rowData.meganetLensRxOrderId} isSubmitting={isSubmitting} onCancel={handleCancel} />
                                                        </FormControl>
                                                        :
                                                        <FormControl className={classes.formControl}>
                                                            <Grid container
                                                                direction='row'
                                                                justify='center'
                                                                alignItems='flex-end'>
                                                                <Grid item>
                                                                    <EditDeleteButtons meganetLensRxOrderId={props.rowData.meganetLensRxOrderId} onEdit={handleEdit} onDelete={handleDelete} />
                                                                </Grid>
                                                                <Grid item>
                                                                    <UploadButton rowData={props.rowData} onUpload={handleUpload} isLoading={props.isLoading} />
                                                                </Grid>
                                                            </Grid>
                                                        </FormControl>
                                                    :
                                                    <>
                                                    </>
                                                    }
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Form>
                        )}
                    </Formik>
                </TableCell>
            </TableRow >
        </>
    )

}) as React.FC<Props>
